import { createFileRoute } from '@tanstack/react-router';
import _ from 'lodash';
import React from 'react';
import TablerIconArrowRight from '~icons/tabler/arrow-right';

import { Fade, Grid, Stack, Typography } from 'gooey';
import { GlowCard } from '#/components/design';
import { Img } from '#/components/images';
import { ButtonLink, Link } from '#/components/links';
import { Page, PageSection, PageSectionTitle } from '#/components/pages';
import fx, { type CustomerStory } from '#/fx';
import { Content, type ExtractLoaderData, pageSEO } from '#/lib/content';
import { canonicalLinkTag } from '#/lib/head';

export const Route = createFileRoute('/_main/customers/')({
  loader: async () => {
    const { stories, page } = await load();
    return { stories, page };
  },
  head: ({ match, loaderData }) => ({
    meta: pageSEO(
      {
        title: 'Customers',
        description: 'Learn about how our customers use our products and services.',
        keywords: ['customers', 'stories'],
      },
      match,
      loaderData?.page
    ),
    links: [canonicalLinkTag(match.pathname)],
  }),
  component: CustomerListPage,
});

async function load() {
  const stories = await fx.listCustomerStories();
  const page = await fx.getPageContent({
    data: {
      key: 'customers',
    },
  });
  return { stories, page };
}

type LoaderData = ExtractLoaderData<typeof load>;

function CustomerListPage() {
  const { stories, page } = Route.useLoaderData();

  const computedStories = React.useMemo(() => {
    return _.chain(page.sections.stories)
      .map((story) => {
        const foundStory = stories.find((s) => s.id === story.id);
        if (!foundStory) {
          return null;
        }
        return {
          ...foundStory,
          order: story.order,
        };
      })
      .compact()
      .sortBy('order')
      .value();
  }, [page, stories]);

  return (
    <Page className='overflow-hidden relative'>
      <PageSectionHero {...page.sections.hero} />
      <PageSectionStories stories={computedStories} />
      <PageSectionCta {...page.sections.callToAction} />
    </Page>
  );
}

function PageSectionHero({ title, subtitle }: LoaderData['page']['sections']['hero']) {
  return (
    <PageSection className='pt-12 md:pt-16' disableGutters>
      <PageSectionTitle
        slotProps={{
          title: {
            variant: 'h1',
          },
          iconAvatar: {
            animation: 'launch',
            className: '[&_svg]:-rotate-45',
          },
        }}
        title={title}
        subtitle={subtitle}
      />
    </PageSection>
  );
}

function PageSectionStories({ stories }: { stories: CustomerStory[] }) {
  return (
    <PageSection disableGutters className='py-16'>
      <Grid
        container
        spacing={{
          xs: 2,
          md: 4,
        }}
      >
        {stories.map((story) => (
          <Grid
            key={story.id}
            size={{
              xs: 12,
              sm: 6,
              md: 4,
            }}
          >
            <Fade in timeout={100}>
              {(transitionProps) => (
                <div {...transitionProps}>
                  <Link to='/customers/$storyId' params={{ storyId: story.id }}>
                    <GlowCard className='flex flex-col items-center justify-center p-4 min-h-64 md:min-h-96 relative group hover:glow-xl glow-primary'>
                      <Img
                        location='static'
                        className='w-2/3 h-auto'
                        src={story.company.logo.src}
                        alt={story.company.name}
                        height={story.company.logo.height}
                        width={story.company.logo.width}
                      />
                      <div className='absolute bottom-8 left-0 right-0 text-center opacity-0 group-hover:opacity-100 transition-opacity duration-300'>
                        <Stack
                          direction='row'
                          spacing={1}
                          className='text-gray-300 w-full mx-auto'
                          alignItems='center'
                          justifyContent='center'
                        >
                          <div>Read their story</div>
                          <TablerIconArrowRight />
                        </Stack>
                      </div>
                    </GlowCard>
                  </Link>
                </div>
              )}
            </Fade>
          </Grid>
        ))}
      </Grid>
    </PageSection>
  );
}

function PageSectionCta({ title, ctas }: LoaderData['page']['sections']['callToAction']) {
  return (
    <PageSection>
      <GlowCard border='top' className='text-center' gutter='xl'>
        <Typography variant='h3' component='h3' className='text-white mb-16'>
          <Content content={title} />
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent='center'>
          <ButtonLink
            variant='contained'
            to={ctas.primary.url}
            endIcon={<TablerIconArrowRight />}
            size='large'
          >
            {ctas.primary.label}
          </ButtonLink>
          {ctas.secondary && (
            <ButtonLink variant='outlined' to={ctas.secondary.url} size='large'>
              {ctas.secondary.label}
            </ButtonLink>
          )}
        </Stack>
      </GlowCard>
    </PageSection>
  );
}
